import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { Box } from '@/lib/api/boxes';
import {
  addAlertBox,
  cancelSnooze,
  removeAlertBox,
  snoozeAlertBox,
  type AlertBox,
} from '@/lib/api/monitoring';

const SNOOZE_OPTIONS = [
  { label: '10 min', value: '10' },
  { label: '30 min', value: '30' },
  { label: '1 hour', value: '60' },
];

type Props = {
  alertBoxes: AlertBox[] | undefined;
  boxes: Box[] | undefined;
  now: number;
};

export function BoxesTab({ alertBoxes, boxes, now }: Props) {
  const [boxToAdd, setBoxToAdd] = useState('');

  const monitoredIps = useMemo(
    () => new Set((alertBoxes ?? []).map((b) => b.ip)),
    [alertBoxes]
  );

  const statusByIp = useMemo(() => {
    const map: Record<string, string> = {};
    boxes?.forEach((b) => {
      if (b?.ip) map[b.ip] = b.status;
    });
    return map;
  }, [boxes]);

  // Boxes available to add (not already monitored).
  const addableBoxes = useMemo(
    () =>
      (boxes ?? [])
        .filter((b) => b?.ip && !monitoredIps.has(b.ip))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [boxes, monitoredIps]
  );

  // Failing boxes first, then unknown, then online; alphabetical within a group.
  const sortedMonitored = useMemo(() => {
    const rank = (ip: string) => {
      const s = statusByIp[ip];
      if (s === 'COMMAND_FAIL' || s === 'PING_FAIL') return 0;
      if (s === 'ONLINE') return 2;
      return 1;
    };
    return [...(alertBoxes ?? [])].sort(
      (a, b) => rank(a.ip) - rank(b.ip) || a.name.localeCompare(b.name)
    );
  }, [alertBoxes, statusByIp]);

  const onAddBox = (ip: string) => {
    const box = addableBoxes.find((b) => b.ip === ip);
    if (box) addAlertBox(box.ip, box.name);
    setBoxToAdd('');
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Monitored boxes</h2>
          <p className="text-sm text-muted-foreground">
            Only these boxes can raise an SMS alert. Snooze silences one box
            temporarily without taking it off the list.
          </p>
        </div>
        <Select value={boxToAdd} onValueChange={onAddBox}>
          <SelectTrigger className="w-[260px]">
            <SelectValue placeholder="Add a box to monitor…" />
          </SelectTrigger>
          <SelectContent>
            {addableBoxes.length === 0 ? (
              <SelectItem value="__none" disabled>
                All boxes are monitored
              </SelectItem>
            ) : (
              addableBoxes.map((b) => (
                <SelectItem key={b.ip} value={b.ip}>
                  {b.name} ({b.ip})
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Box</TableHead>
              <TableHead>IP</TableHead>
              <TableHead>Current status</TableHead>
              <TableHead>Snooze</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedMonitored.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  No boxes monitored yet.
                </TableCell>
              </TableRow>
            ) : (
              sortedMonitored.map((b) => {
                const snoozed =
                  !!b.snoozeUntil && b.snoozeUntil.getTime() > now;
                return (
                  <TableRow key={b.ip}>
                    <TableCell className="font-medium">{b.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {b.ip}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          statusByIp[b.ip] === 'COMMAND_FAIL' ||
                          statusByIp[b.ip] === 'PING_FAIL'
                            ? 'destructive'
                            : 'outline'
                        }
                      >
                        {statusByIp[b.ip] ?? '—'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {snoozed ? (
                        <span className="text-sm text-muted-foreground">
                          until {format(b.snoozeUntil as Date, 'HH:mm')}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        {snoozed ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => cancelSnooze(b.ip)}
                          >
                            Cancel snooze
                          </Button>
                        ) : (
                          <Select
                            value=""
                            onValueChange={(v) =>
                              snoozeAlertBox(b.ip, Number(v))
                            }
                          >
                            <SelectTrigger className="h-8 w-[120px]">
                              <SelectValue placeholder="Snooze" />
                            </SelectTrigger>
                            <SelectContent>
                              {SNOOZE_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                  {o.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAlertBox(b.ip)}
                        >
                          Remove
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
